import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#startAstraAutoplay(signal);
    this.addEventListener('slideshow:select', (event) => {
      if (event.target !== this.slideshow) return;
      const counter = this.querySelector('[data-aa-gallery-current]');
      if (counter) counter.textContent = String(event.detail.index + 1);
    }, { signal });
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #astraTimer;
  #astraObserver;
  #astraVisible = false;
  #astraHasAdvanced = false;

  #clearAstraTimer() {
    clearTimeout(this.#astraTimer);
    this.#astraTimer = undefined;
  }

  #startAstraAutoplay(signal) {
    const product = this.dataset.astraAutoplay;
    if (!product || this.hasAttribute('data-astra-guide-first')) return;
    const stopped = window.__astraGalleryAutoplayStopped ||= new Set();
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const stop = () => {
      stopped.add(product);
      this.#clearAstraTimer();
    };
    const update = () => {
      this.#clearAstraTimer();
      if (stopped.has(product) || motion.matches || document.hidden || !this.#astraVisible) return;
      this.#astraTimer = setTimeout(() => {
        const slideshow = this.slideshow;
        const count = slideshow?.slides?.length || 0;
        if (count > 1) {
          slideshow.select((slideshow.current + 1) % count);
          this.#astraHasAdvanced = true;
        }
        update();
      }, this.#astraHasAdvanced ? 3000 : 1500);
    };
    // Interaction ends autoplay for this page visit, including after Shopify remounts the gallery.
    for (const event of ['pointerdown', 'touchstart', 'click', 'focusin', 'keydown', 'play']) {
      this.addEventListener(event, stop, { signal, capture: true, passive: true });
    }
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse' || event.pointerType === 'pen') stop();
    }, { signal });
    document.addEventListener('visibilitychange', update, { signal });
    motion.addEventListener('change', update, { signal });
    this.#astraObserver = new IntersectionObserver(([entry]) => {
      this.#astraVisible = entry.isIntersecting;
      update();
    }, { threshold: 0.1 });
    this.#astraObserver.observe(this);
  }


  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#clearAstraTimer();
    this.#astraObserver?.disconnect();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  nextMedia(event) {
    this.slideshow?.next(event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
